import React, { useState, useCallback, useRef, useEffect } from 'react';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import BookmarkIcon from '@mui/icons-material/Bookmark';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import SaveAltIcon from '@mui/icons-material/SaveAlt';

/**
 * SavedViewsPanel
 *
 * Renders inside the ProjectHomeFilterDrawer as the fifth section.
 * Displays the user's saved filter views for the current project with
 * actions to apply, set as default, rename (inline), and delete each view.
 * Also provides a "Save current filters as view" inline form.
 *
 * Props:
 *   views          — array of view objects from backend
 *   onApplyView    — (view) => void — applies a saved view's filter_state
 *   onSaveView     — (name, isDefault) => Promise<void> — saves current filters as a new named view
 *   onSetDefault   — (view) => Promise<void> — toggles is_default on a view
 *   onRenameView   — (view, newName) => Promise<void> — renames an existing view
 *   onDeleteView   — (view) => Promise<void> — deletes a view
 *   saving         — boolean — true while a create/update/delete is in flight
 */
const SavedViewsPanel = ({
  views = [],
  onApplyView,
  onSaveView,
  onSetDefault,
  onRenameView,
  onDeleteView,
  saving = false,
}) => {
  const [saveName, setSaveName]           = useState('');
  const [saveAsDefault, setSaveAsDefault] = useState(false);
  const [saveNameError, setSaveNameError] = useState(false); // true after bookmark/save attempt with empty name
  const saveNameInputRef                  = useRef(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // view id pending confirmation
  const [editingId, setEditingId]         = useState(null); // view id currently being renamed
  const [editName, setEditName]           = useState('');   // draft name for the view being edited
  const [editError, setEditError]         = useState('');   // error message while in edit mode
  const editInputRef                      = useRef(null);

  // Focus the edit input whenever we enter edit mode for a view
  useEffect(() => {
    if (editingId !== null) {
      // rAF gives the TextField time to mount before we try to focus it
      requestAnimationFrame(() => editInputRef.current?.focus());
    }
  }, [editingId]);

  const handleStartEdit = useCallback((view) => {
    setEditingId(view.id);
    setEditName(view.name);
    setEditError('');
    setDeleteConfirm(null); // clear any pending delete confirmation
  }, []);

  const handleCancelEdit = useCallback(() => {
    setEditingId(null);
    setEditName('');
    setEditError('');
  }, []);

  const handleConfirmEdit = useCallback(async (view) => {
    const trimmed = editName.trim();
    if (!trimmed || trimmed === view.name) {
      handleCancelEdit();
      return;
    }
    setEditError('');
    try {
      await onRenameView(view, trimmed);
      // Success — close the edit field
      setEditingId(null);
      setEditName('');
    } catch {
      // Failure — keep the field open and show an inline error
      setEditError('Failed to rename. Try again.');
      requestAnimationFrame(() => editInputRef.current?.focus());
    }
  }, [editName, onRenameView, handleCancelEdit]);

  const handleEditKeyDown = useCallback((e, view) => {
    if (e.key === 'Enter')  handleConfirmEdit(view);
    if (e.key === 'Escape') handleCancelEdit();
  }, [handleConfirmEdit, handleCancelEdit]);

  const handleSave = useCallback(async () => {
    const trimmed = saveName.trim();
    if (!trimmed) {
      setSaveNameError(true);
      requestAnimationFrame(() => saveNameInputRef.current?.focus());
      return;
    }
    await onSaveView(trimmed, saveAsDefault);
    setSaveName('');
    setSaveAsDefault(false);
    setSaveNameError(false);
  }, [saveName, saveAsDefault, onSaveView]);

  const handleToggleDefault = useCallback(() => {
    const willBeDefault = !saveAsDefault;
    setSaveAsDefault(willBeDefault);
    // If toggling default on with no name, flag the field and focus it
    if (willBeDefault && !saveName.trim()) {
      setSaveNameError(true);
      requestAnimationFrame(() => saveNameInputRef.current?.focus());
    }
  }, [saveAsDefault, saveName]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter') handleSave();
  }, [handleSave]);

  const handleDeleteClick = useCallback((view) => {
    if (deleteConfirm === view.id) {
      onDeleteView(view);
      setDeleteConfirm(null);
    } else {
      setDeleteConfirm(view.id);
      setEditingId(null); // cancel any in-progress edit
    }
  }, [deleteConfirm, onDeleteView]);

  return (
    <Box>
      {/* ── Save current filters ──────────────────────────────────── */}
      <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
        <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 10 }}>
          Save current filters as view
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, mt: 0.75, alignItems: 'flex-start' }}>
          <TextField
            size="small"
            placeholder="View name"
            value={saveName}
            inputRef={saveNameInputRef}
            onChange={(e) => { setSaveName(e.target.value); if (e.target.value.trim()) setSaveNameError(false); }}
            onKeyDown={handleKeyDown}
            disabled={saving}
            inputProps={{ maxLength: 100 }}
            error={saveNameError}
            helperText={saveNameError ? 'Enter a name to save this view' : undefined}
            sx={{ flex: 1, '& .MuiInputBase-root': { fontSize: 13 } }}
            InputProps={{
              endAdornment: saveName.trim() ? (
                <InputAdornment position="end">
                  <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: 11 }}>
                    {saveName.trim().length}/100
                  </Typography>
                </InputAdornment>
              ) : null,
            }}
          />
          <Tooltip title={saveAsDefault ? 'Will be set as default' : 'Set as default'}>
            <IconButton
              size="small"
              onClick={handleToggleDefault}
              disabled={saving}
              sx={{ color: saveAsDefault ? 'primary.main' : 'action.disabled', mt: 0.25 }}
              aria-label={saveAsDefault ? 'Will be saved as default view' : 'Save as default view'}
            >
              {saveAsDefault ? <BookmarkIcon fontSize="small" /> : <BookmarkBorderIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
          <Button
            size="small"
            variant="contained"
            disableElevation
            disabled={saving}
            onClick={handleSave}
            startIcon={saving ? <CircularProgress size={12} color="inherit" /> : <SaveAltIcon fontSize="small" />}
            sx={{ fontSize: 12, px: 1.5, mt: 0.25, whiteSpace: 'nowrap' }}
          >
            Save
          </Button>
        </Box>
        {saveAsDefault && (
          <Typography variant="caption" sx={{ color: 'primary.main', mt: 0.5, display: 'block', fontSize: 11 }}>
            This view will load automatically when you open this project.
          </Typography>
        )}
      </Box>

      {/* ── Saved views list ──────────────────────────────────────── */}
      {views.length > 0 && (
        <>
          <Divider sx={{ mx: 2, my: 0.5 }} />
          <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 10, px: 2, pt: 0.75, display: 'block' }}>
            Saved views
          </Typography>
          <List dense disablePadding sx={{ pb: 0.5, mx: 1.5 }}>
            {views.map((view) => (
              <ListItem
                key={view.id}
                disablePadding
                secondaryAction={
                  editingId === view.id ? (
                    /* ── Edit mode action buttons ── */
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                      <Tooltip title="Save name">
                        <IconButton
                          size="small"
                          onClick={() => handleConfirmEdit(view)}
                          disabled={saving || !editName.trim()}
                          aria-label="Save renamed view"
                          sx={{ color: 'primary.main' }}
                        >
                          <CheckIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Cancel">
                        <IconButton
                          size="small"
                          onClick={handleCancelEdit}
                          aria-label="Cancel rename"
                          sx={{ color: 'action.disabled' }}
                        >
                          <CloseIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  ) : (
                    /* ── Normal mode action buttons ── */
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                      <Tooltip title={view.is_default ? 'Default view (click to unset)' : 'Set as default'}>
                        <IconButton
                          size="small"
                          onClick={() => onSetDefault(view)}
                          disabled={saving}
                          aria-label={view.is_default ? 'Unset default view' : 'Set as default view'}
                          sx={{ color: view.is_default ? 'primary.main' : 'action.disabled' }}
                        >
                          {view.is_default ? <BookmarkIcon sx={{ fontSize: 16 }} /> : <BookmarkBorderIcon sx={{ fontSize: 16 }} />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Rename view">
                        <IconButton
                          size="small"
                          onClick={() => handleStartEdit(view)}
                          disabled={saving}
                          aria-label="Rename saved view"
                          sx={{ color: 'action.disabled', '&:hover': { color: 'text.primary' } }}
                        >
                          <EditOutlinedIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={deleteConfirm === view.id ? 'Click again to confirm delete' : 'Delete view'}>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteClick(view)}
                          disabled={saving}
                          aria-label="Delete saved view"
                          sx={{ color: deleteConfirm === view.id ? 'error.main' : 'action.disabled' }}
                        >
                          <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )
                }
                sx={{ pr: editingId === view.id ? 9 : 11 }}
              >
                {editingId === view.id ? (
                  /* ── Inline rename input ── */
                  <Box sx={{ px: 2, py: 0.5, flex: 1 }}>
                    <TextField
                      inputRef={editInputRef}
                      size="small"
                      value={editName}
                      onChange={(e) => { setEditName(e.target.value); setEditError(''); }}
                      onKeyDown={(e) => handleEditKeyDown(e, view)}
                      disabled={saving}
                      inputProps={{ maxLength: 100 }}
                      error={!!editError}
                      helperText={editError || undefined}
                      sx={{ width: '100%', '& .MuiInputBase-root': { fontSize: 13 } }}
                      autoComplete="off"
                    />
                  </Box>
                ) : (
                  /* ── Normal list item button ── */
                  <ListItemButton
                    dense
                    onClick={() => onApplyView(view)}
                    sx={{ borderRadius: 1.5, py: 0.4, px: 1.5 }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <Typography variant="body2" sx={{ fontSize: 13, fontWeight: view.is_default ? 600 : 400 }}>
                            {view.name}
                          </Typography>
                          {view.is_default && (
                            <Chip
                              label="default"
                              size="small"
                              color="primary"
                              variant="outlined"
                              sx={{ height: 16, fontSize: 10, '& .MuiChip-label': { px: 0.75 } }}
                            />
                          )}
                        </Box>
                      }
                    />
                  </ListItemButton>
                )}
              </ListItem>
            ))}
          </List>
          {deleteConfirm !== null && (
            <Typography
              variant="caption"
              sx={{ color: 'error.main', px: 2, pb: 0.75, display: 'block', fontSize: 11 }}
            >
              Click the delete icon again to confirm.
            </Typography>
          )}
        </>
      )}

      {views.length === 0 && (
        <Typography variant="caption" sx={{ color: 'text.disabled', px: 2, pb: 1, display: 'block', fontSize: 12 }}>
          No saved views yet. Set your filters and save them above.
        </Typography>
      )}
    </Box>
  );
};

export default SavedViewsPanel;
